import React, { useEffect, useState } from 'react';
import {
  RadialBarChart, RadialBar, PolarAngleAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const RISK_SCORE_MAX = 20; // max possible (diabetes 6 + heart 8 + hbp 6 = 20)

function formatINR(n) {
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  return `₹${n.toLocaleString('en-IN')}`;
}

function RiskMeter({ score, label }) {
  const pct = Math.min((score / RISK_SCORE_MAX) * 100, 100);
  const needleLeft = `${pct}%`;

  return (
    <div className="risk-meter">
      <div className="risk-meter-track">
        <div className="risk-meter-needle" style={{ left: needleLeft }} />
      </div>
      <div className="risk-meter-labels">
        <span className="risk-meter-label">Low Risk</span>
        <span className="risk-meter-label">Moderate</span>
        <span className="risk-meter-label">High Risk</span>
      </div>
    </div>
  );
}

const RISK_COLOR = { high: '#ef4444', medium: '#f59e0b', low: '#10b981' };

export default function ResultPanel({ result, form, onReset }) {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    setTimeout(() => setAnimated(true), 200);
  }, []);

  const { annual_premium, monthly_premium, risk_score, risk_label, risk_factors, confidence_band } = result;

  // Bar chart data for risk factors
  const chartData = risk_factors
    .filter(f => f.factor !== 'No significant risk factors')
    .map(f => ({
      name: f.factor.length > 18 ? f.factor.slice(0, 18) + '…' : f.factor,
      impact: f.impact === 'high' ? 3 : f.impact === 'medium' ? 2 : 1,
      fill: RISK_COLOR[f.impact],
    }));

  const handleDownload = () => {
    const lines = [
      'INSURANCE PREMIUM ESTIMATE REPORT',
      '===================================',
      `Date: ${new Date().toLocaleDateString('en-IN')}`,
      '',
      'YOUR PROFILE',
      `Age: ${form.age}`,
      `Gender: ${form.gender}`,
      `Marital Status: ${form.marital_status}`,
      `Dependants: ${form.number_of_dependants}`,
      `Region: ${form.region}`,
      `Employment: ${form.employment_status}`,
      `Annual Income: ₹${form.income_lakhs}L`,
      `BMI Category: ${form.bmi_category}`,
      `Smoking: ${form.smoking_status}`,
      `Physical Activity: ${form.physical_activity}`,
      `Stress Level: ${form.stress_level}`,
      `Medical History: ${form.medical_history}`,
      `Insurance Plan: ${form.insurance_plan}`,
      '',
      'PREDICTION RESULTS',
      `Annual Premium: ₹${annual_premium.toLocaleString('en-IN')}`,
      `Monthly Premium: ₹${monthly_premium.toLocaleString('en-IN')}`,
      `Risk Score: ${risk_score} / ${RISK_SCORE_MAX}`,
      `Risk Level: ${risk_label}`,
      `Confidence Band: ₹${confidence_band.low.toLocaleString('en-IN')} – ₹${confidence_band.high.toLocaleString('en-IN')}`,
      '',
      'RISK FACTORS',
      ...risk_factors.map(f => `  • ${f.factor} (${f.impact.toUpperCase()} impact)`),
      '',
      '---',
      'This estimate is generated by an XGBoost ML model (R² = 0.9944).',
      'Actual premiums may vary based on insurer-specific criteria.',
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'premium-estimate.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="result-shell app-shell">
      <div className="bg-orb bg-orb-1" />
      <div className="bg-orb bg-orb-2" />

      <div className="result-container">
        {/* Header */}
        <div className="result-header">
          <button className="result-back" onClick={onReset}>
            ← New Estimate
          </button>
          <div className="header-badge">
            <span className="badge-dot" />
            XGBoost · R² 99.4%
          </div>
        </div>

        {/* Premium Hero */}
        <div className="premium-hero">
          <div className="premium-label">Your Estimated Annual Premium</div>
          <div className="premium-amount">
            <span className="premium-currency">₹</span>
            {annual_premium.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </div>
          <div className="premium-period">per year · {form.insurance_plan} Plan</div>
          <div className="premium-band">
            📊 Range: ₹{confidence_band.low.toLocaleString('en-IN', { maximumFractionDigits: 0 })} – ₹{confidence_band.high.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </div>
          <div className="monthly-breakdown">
            <span>≈</span>
            <span className="monthly-amount">₹{monthly_premium.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
            <span>/ month</span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-value" style={{ color: RISK_COLOR[risk_label === 'Very High' ? 'high' : risk_label === 'High' ? 'high' : risk_label === 'Moderate' ? 'medium' : 'low'] }}>
              {risk_label}
            </div>
            <div className="stat-label">Risk Level</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-value">{risk_score}<span style={{ fontSize: '0.75rem', color: 'var(--c-text-3)', fontWeight: 400 }}>/{RISK_SCORE_MAX}</span></div>
            <div className="stat-label">Risk Score</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📈</div>
            <div className="stat-value">{risk_factors.length}</div>
            <div className="stat-label">Risk Factors</div>
          </div>
        </div>

        {/* Risk Meter */}
        <div className="risk-meter-card">
          <div className="card-title">
            <span>🎯</span> Risk Assessment Meter
          </div>
          <RiskMeter score={risk_score} label={risk_label} />
          <div style={{ fontSize: '0.82rem', color: 'var(--c-text-2)', marginTop: '0.75rem', lineHeight: 1.6 }}>
            Your overall medical risk score is <strong>{risk_score}/{RISK_SCORE_MAX}</strong> — rated <strong style={{ color: RISK_COLOR[risk_label.toLowerCase().includes('high') ? 'high' : risk_label === 'Moderate' ? 'medium' : 'low'] }}>{risk_label}</strong>. This score is computed from your medical history using disease-severity weights.
          </div>
        </div>

        {/* Risk Factors */}
        <div className="risk-factors-card">
          <div className="card-title">
            <span>⚠️</span> Factors Influencing Your Premium
          </div>
          <div className="risk-list">
            {risk_factors.map((f, i) => (
              <div key={i} className="risk-item">
                <span className="risk-item-icon">{f.icon}</span>
                <span className="risk-item-name">{f.factor}</span>
                <span className={`risk-pill ${f.impact}`}>{f.impact}</span>
              </div>
            ))}
          </div>

          {chartData.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--c-text-2)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Impact Breakdown
              </div>
              <ResponsiveContainer width="100%" height={Math.max(chartData.length * 44, 80)}>
                <BarChart
                  layout="vertical"
                  data={chartData}
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                >
                  <XAxis type="number" domain={[0, 3]} hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={130}
                    tick={{ fontSize: 12, fill: '#475569' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(v) => [['Low', 'Medium', 'High'][v - 1] + ' Impact', 'Level']}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                  />
                  <Bar dataKey="impact" radius={[0, 4, 4, 0]} barSize={16}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="cta-row">
          <button className="btn btn-submit" onClick={() => alert('Contact an insurance advisor to get the best plan for your profile!')}>
            🛡️ Get Insurance Plan
          </button>
          <button className="btn btn-ghost" onClick={handleDownload}>
            ⬇️ Download Report
          </button>
        </div>

        <div className="app-footer">
          <p>Powered by XGBoost ML model · R² = 0.9944 · Estimates are indicative only. Actual premiums vary by insurer.</p>
        </div>
      </div>
    </div>
  );
}
